import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { AlertCircle, ArrowLeft, Lock, MessageSquareMore } from "lucide-react";
import logo from "@/assets/logo.svg";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../components/ui/hover-card";
import {
  confirmPublicDraftOrder,
  createPublicDraftOrder,
  fetchPublicDraft,
  fetchPublicDraftOrderSessionStatus,
  PublicDraftInfo,
  unlockPublicDraft,
  WorkflowApiError,
} from "../services/workflow";
import { defaultOrderConsentPayload, getMissingOrderConsentLabels } from "../order-consent";
import { Seo } from "../components/Seo";
import { isStripeCheckoutAccepted, isStripePaymentConfirmed } from "../services/workflow-ui";

export function DraftPreview() {
  const { draftId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [draft, setDraft] = useState<PublicDraftInfo | null>(null);
  const [unlockedDraft, setUnlockedDraft] = useState<PublicDraftInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [isStartingCheckout, setIsStartingCheckout] = useState(false);
  const [isConfirmingCheckout, setIsConfirmingCheckout] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [error, setError] = useState("");
  const consents = defaultOrderConsentPayload;
  const checkoutSessionId = searchParams.get("session_id");
  const seo = (
    <Seo
      title="Entwurfsvorschau"
      description="Geschützte Vorschau eines Website-Entwurfs."
      path={draftId ? `/preview/${draftId}` : "/preview"}
      noIndex
    />
  );

  useEffect(() => {
    let isMounted = true;

    const loadDraft = async () => {
      if (!draftId) {
        setError("Entwurf nicht gefunden");
        setIsLoading(false);
        return;
      }

      try {
        const nextDraft = await fetchPublicDraft(draftId);
        if (isMounted) {
          setDraft(nextDraft);
        }
      } catch (loadError) {
        if (isMounted) {
          setError(
            loadError instanceof WorkflowApiError
              ? loadError.message
              : "Entwurf konnte nicht geladen werden",
          );
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadDraft();

    return () => {
      isMounted = false;
    };
  }, [draftId]);

  useEffect(() => {
    if (!draftId || !checkoutSessionId) {
      return;
    }

    let isMounted = true;
    setIsConfirmingCheckout(true);
    setError("");

    const finalizeReturn = async () => {
      try {
        const session = await fetchPublicDraftOrderSessionStatus(draftId, checkoutSessionId);

        if (!isMounted) {
          return;
        }

        if (isStripeCheckoutAccepted(session.status, session.paymentStatus)) {
          const nextDraft = await confirmPublicDraftOrder(draftId, checkoutSessionId);

          if (!isMounted) {
            return;
          }

          setUnlockedDraft(nextDraft);
          setSuccessMessage(
            isStripePaymentConfirmed(session.paymentStatus)
              ? "Zahlung erfolgreich. Ihr Auftrag wurde verbindlich übernommen."
              : "Bestellung übernommen. Die SEPA-Zahlung wird noch von Stripe verarbeitet.",
          );
        } else {
          setError("Der Checkout wurde noch nicht abgeschlossen. Sie können ihn erneut starten.");
        }
      } catch (checkoutError) {
        if (!isMounted) {
          return;
        }

        setError(
          checkoutError instanceof WorkflowApiError
            ? checkoutError.message
            : "Checkout-Rückkehr konnte nicht verarbeitet werden",
        );
      } finally {
        if (isMounted) {
          setIsConfirmingCheckout(false);
          navigate(`/preview/${draftId}`, { replace: true });
        }
      }
    };

    finalizeReturn();

    return () => {
      isMounted = false;
    };
  }, [checkoutSessionId, draftId, navigate]);

  const handleUnlock = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!draftId) {
      return;
    }

    setError("");
    setIsUnlocking(true);

    try {
      const nextDraft = await unlockPublicDraft(draftId, password);
      setUnlockedDraft(nextDraft);
    } catch (unlockError) {
      setError(
        unlockError instanceof WorkflowApiError
          ? unlockError.message
          : "Entwurf konnte nicht entsperrt werden",
      );
    } finally {
      setIsUnlocking(false);
    }
  };

  const handleStartCheckout = async () => {
    if (!draftId || !unlockedDraft || isStartingCheckout || isConfirmingCheckout) {
      return;
    }

    setError("");
    setSuccessMessage("");
    setIsStartingCheckout(true);

    try {
      const missingConsents = getMissingOrderConsentLabels(consents);
      if (missingConsents.length > 0) {
        throw new WorkflowApiError(
          `Bitte bestätigen Sie vor dem Checkout noch: ${missingConsents.join(", ")}.`,
        );
      }

      const session = await createPublicDraftOrder(draftId, consents);

      if (!session.url) {
        throw new Error("Stripe hat keine Checkout-URL geliefert");
      }

      window.location.assign(session.url);
    } catch (checkoutError) {
      setError(
        checkoutError instanceof WorkflowApiError
          ? checkoutError.message
          : "Stripe-Checkout konnte nicht vorbereitet werden",
      );
      setIsStartingCheckout(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        {seo}
        <p className="text-sm text-gray-500">Entwurf wird geladen...</p>
      </div>
    );
  }

  if (error && !draft) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        {seo}
        <div className="max-w-md rounded-2xl border border-red-200 bg-white p-8 text-center shadow-sm">
          <AlertCircle className="mx-auto mb-4 text-red-500" size={42} />
          <h1 className="text-2xl font-bold text-gray-900">Entwurf nicht gefunden</h1>
          <p className="mt-2 text-sm leading-relaxed text-gray-600">{error}</p>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="mt-6 rounded-lg bg-black px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800"
          >
            Zur Startseite
          </button>
        </div>
      </div>
    );
  }

  if (isConfirmingCheckout && !unlockedDraft) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4">
        {seo}
        <div className="w-full max-w-lg rounded-3xl border border-white/10 bg-white/5 p-8 text-center text-white backdrop-blur">
          <h1 className="text-2xl font-semibold">Zahlung wird bestätigt</h1>
          <p className="mt-3 text-sm text-white/70">
            Stripe hat den Checkout abgeschlossen. Ihr Auftrag wird jetzt übernommen oder der
            Status aktualisiert.
          </p>
        </div>
      </div>
    );
  }

  if (!unlockedDraft) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        {seo}
        <div className="w-full max-w-lg rounded-3xl bg-white p-8 shadow-xl">
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-black text-white">
              <Lock size={28} />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Geschützter Entwurf</h1>
            <p className="mt-2 text-gray-600">{draft?.title}</p>
          </div>

          <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm leading-relaxed text-blue-900">
            Hier wird das Entwurfs-Passwort benötigt, nicht das Portal-Passwort für den Login.
            Nach dem Entsperren öffnet sich der interaktive Entwurf.
          </div>

          <form onSubmit={handleUnlock} className="mt-6 space-y-4">
            <label className="block space-y-2 text-sm text-gray-700">
              <span>Entwurfspasswort</span>
              <input
                type="text"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
                placeholder="Entwurfs-Passwort aus der E-Mail"
                autoFocus
              />
            </label>

            {error ? (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={isUnlocking}
              className="w-full rounded-xl bg-black px-4 py-3 text-sm font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isUnlocking ? "Prüft..." : "Entwurf entsperren"}
            </button>
          </form>

          <button
            type="button"
            onClick={() => navigate("/")}
            className="mt-6 inline-flex items-center gap-2 text-sm text-gray-600 hover:text-black"
          >
            <ArrowLeft size={16} />
            Zurück
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950">
      {seo}
      <div className="border-b border-white/10 bg-black/80 text-white backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 py-2.5 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <img src={logo} alt="mutzur" className="h-5 w-auto brightness-0 invert" />
            <span className="rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-[11px] font-medium uppercase tracking-[0.12em] text-white/80">
              Entwurf
            </span>
            {unlockedDraft.personalMessage ? (
              <HoverCard openDelay={120}>
                <HoverCardTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-full border border-sky-400/30 bg-sky-400/10 px-2.5 py-1 text-[11px] font-medium text-sky-100 transition hover:bg-sky-400/15"
                  >
                    <MessageSquareMore size={14} />
                    Nachricht von mutzur
                  </button>
                </HoverCardTrigger>
                <HoverCardContent
                  align="start"
                  className="w-[22rem] border-white/15 bg-[#020617] text-white shadow-2xl"
                >
                  <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-sky-200/80">
                    Persönliche Nachricht
                  </p>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-white/90">
                    {unlockedDraft.personalMessage}
                  </p>
                </HoverCardContent>
              </HoverCard>
            ) : null}
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={() => navigate("/ablauf")}
              className="inline-flex items-center justify-center rounded-lg border border-white/15 px-3 py-2 text-xs font-medium text-white transition hover:bg-white/10"
            >
              Wie geht es weiter?
            </button>
            <button
              type="button"
              onClick={() => {
                void handleStartCheckout();
              }}
              disabled={isStartingCheckout || isConfirmingCheckout}
              className="inline-flex items-center justify-center rounded-lg bg-white px-3 py-2 text-xs font-semibold text-black transition hover:bg-white/90"
            >
              {isStartingCheckout ? "Formular wird vorbereitet..." : "Jetzt bestellen"}
            </button>
          </div>
        </div>
      </div>
      {successMessage ? (
        <div className="border-b border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
          <div className="mx-auto max-w-7xl">{successMessage}</div>
        </div>
      ) : null}
      {error ? (
        <div className="border-b border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-100">
          <div className="mx-auto max-w-7xl">{error}</div>
        </div>
      ) : null}
      {isConfirmingCheckout ? (
        <div className="border-b border-blue-500/20 bg-blue-500/10 px-4 py-3 text-sm text-blue-100">
          <div className="mx-auto max-w-7xl">
            Stripe-Zahlung wird geprüft und Ihr Auftrag wird übernommen...
          </div>
        </div>
      ) : null}
      {unlockedDraft.previewUrl ? (
        <iframe
          title={unlockedDraft.title}
          src={unlockedDraft.previewUrl}
          className="h-[calc(100vh-58px)] w-full border-0 bg-white"
        />
      ) : null}
    </div>
  );
}
